import React, { useEffect, useState } from "react";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { RiBarChart2Fill } from "react-icons/ri";
import { ToastContainer, toast } from "react-toastify";
import { useLocation } from "react-router-dom";

import axios, { cookies } from "../../axios/axios";
import Layout from "./Layout";
import Navbar from "../Navbar";
import Footer from "../Footer";
import Order from "./Order";

const DashboardPage = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();

  useEffect(() => {
    fetchOrders();

    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get("payment");
    if (paymentStatus) {
      switch (paymentStatus) {
        case "success":
          handlePaymentSuccess(); // refresh orders here
          break;
        case "error":
          toast.error("Payment failed. Please try again.");
          break;
        case "cancelled":
          toast.info("Payment was cancelled.");
          break;
        default:
          break;
      }
      // clear params so toast doesn’t repeat
      window.history.replaceState({}, document.title, "/dashboard");
    }
  }, [location]);

  const handlePaymentSuccess = async () => {
    try {
      setIsLoading(true);
      await fetchOrders(); // refresh from DB
      toast.success("Payment successful! Your order status has been updated.");
      setSelectedOrder(null); // remove PayPal button after payment
    } catch (error) {
      console.error("Error refreshing orders:", error);
      toast.error("Failed to refresh order status");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get("/user/getPendingOrders/");
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error(error.response?.data?.message || "An error occurred");
    }
  };

  const totalImages = orders.reduce(
    (sum, order) => sum + (parseInt(order.quantity, 10) || 0),
    0
  );
  const newOrders = orders.filter((order) => order.status === 1).length;
  const orderDone = orders.filter((order) => order.status === 8).length;

  const getOrderType = (order) => {
    if (!order?.serviceId?.service) return null;
    const service = order.serviceId.service.toLowerCase();
    if (service.includes("real estate")) return "realestate";
    if (service.includes("retouching")) return "retouching";
    if (service.includes("color correction")) return "colorcorrection";
    if (service.includes("video editing")) return "videoediting";
    if (service.includes("smart album")) return "smartalbum";
    if (service.includes("wedding album")) return "weddingalbum";
    if (service.includes("culling")) return "culling";
    return null;
  };

  const handlePayPalApprove = async (data, actions) => {
    try {
      setIsLoading(true);
      const orderType = getOrderType(selectedOrder);

      if (!orderType) {
        toast.error("Invalid order type");
        return;
      }

      if (!selectedOrder._id) {
        toast.error("Invalid order ID");
        return;
      }

      console.log("Initiating payment for order:", {
        orderId: selectedOrder._id,
        orderType,
        amount: selectedOrder.price,
      });

      const response = await axios.post("/payment/pay", {
        amount: selectedOrder.price,
        orderId: selectedOrder._id,
        orderType: orderType,
      });

      if (!response.data.approvalUrl) {
        throw new Error("No approval URL received");
      }

      window.location.href = response.data.approvalUrl;
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(
        error.response?.data?.message || "Payment initialization failed"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const user = cookies.get("user");
  const billingType = user?.user?.billingType;

  return (
    <>
      <Navbar />
      <Layout>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
        <div className="bg_maincolor p-4">
          <div className="d-flex align-items-center justify-content-end">
            <div className="dropdownMenu">
              <div className="d-flex align-items-center gap-2 cursor-pointer">
                <div
                  className="rounded-pill p-2"
                  style={{ backgroundColor: "#ccc" }}
                >
                  <img
                    src="https://cdn2.iconfinder.com/data/icons/avatar-114/64/photographer-occupation-profession-traveller-vlogger-utuber-512.png"
                    width={30}
                    alt=""
                  />
                </div>
                <p className="mb-0 text-light">{user.user.fname}</p>
              </div>
            </div>
          </div>
          <div className="row">
            <DashboardCard title="Total Orders" count={orders.length} />
            <DashboardCard title="New Orders" count={newOrders} />
            <DashboardCard title="Total Images" count={totalImages} />
            <DashboardCard title="Order Done" count={orderDone} />
          </div>
        </div>
        <div className="bg-light p-4">
          <h5>Orders</h5>
          <div className="table-responsive">
            <table className="table">
              <tbody>
                <tr className="table-light">
                  <td className="fs-14">Order ID</td>
                  <td className="fs-14">Payment</td>
                  <td className="fs-14">Job Name</td>
                  <td className="fs-14">Service</td>
                  <td className="fs-14">No Of Image</td>
                  <td className="fs-14">Price</td>
                  <td className="fs-14">Message</td>
                  <td className="fs-14">File Location</td>
                  <td className="fs-14">Order Status</td>
                  <td className="fs-14">Action</td>
                </tr>
              </tbody>
              <tbody>
                {orders.map((val, index) => (
                  <Order
                    key={index}
                    val={val}
                    setSelectedOrder={setSelectedOrder}
                    isLoading={isLoading}
                    billingType={billingType}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Show PayPal Button only when a payment is initiated */}
        <div className="col-12 col-md-4 ms-0 ms-md-5 mt-3">
          {selectedOrder && (
            <PayPalScriptProvider
              options={{
                "client-id":
                  "AdU8lu5mvEB3rmd4ch0B30wh4tPghFZyPNLBFy2Bme7fVOUPN7gIndt9D49lxCgDDmlGV0BwDDh8V_Ww",
              }}
            >
              <PayPalButtons
                createOrder={(data, actions) => {
                  return actions.order.create({
                    purchase_units: [
                      { amount: { value: selectedOrder.price } },
                    ],
                  });
                }}
                onApprove={handlePayPalApprove}
                onError={(err) => {
                  console.error("PayPal error:", err);
                  toast.error("Payment failed. Please try again.");
                }}
              />
            </PayPalScriptProvider>
          )}
        </div>
      </Layout>
      <Footer />
    </>
  );
};

const DashboardCard = ({ title, count }) => (
  <div className="col-6 col-md-3 my-4">
    <div className="bg-white p-3 rounded-2">
      <div className="d-flex align-items-center justify-content-between pb-2">
        <div>
          <small className="text-secondary">{title}</small>
          <p className="mb-0">{count}</p>
        </div>
        <div className="orderGradient cursor-pointer">
          <RiBarChart2Fill color="white" />
        </div>
      </div>
    </div>
  </div>
);

export default DashboardPage;
